import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  IndianRupee, Wallet, FileWarning, Eraser, AlertTriangle, Calendar,
  Clock, ExternalLink, User, Tag, Building2, Loader2, ArrowUpRight,
  CheckCircle2, CircleAlert, TrendingUp, Ban, History, FileText, GraduationCap,
} from 'lucide-react';

// ── Formatting helpers ─────────────────────────────────────
const fmtInr = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '₹0';
  const abs = Math.abs(n);
  if (abs >= 1e7) return `${n < 0 ? '-' : ''}₹${(abs / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `${n < 0 ? '-' : ''}₹${(abs / 1e5).toFixed(2)}L`;
  return `${n < 0 ? '-' : ''}₹${Math.round(abs).toLocaleString('en-IN')}`;
};
const fmtInrFull = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const STATUS_STYLES = {
  paid:            { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'PAID' },
  partially_paid:  { cls: 'bg-amber-100 text-amber-800 border-amber-200',       label: 'PARTIALLY PAID' },
  partial:         { cls: 'bg-amber-100 text-amber-800 border-amber-200',       label: 'PARTIAL' },
  unpaid:          { cls: 'bg-rose-100 text-rose-700 border-rose-200',          label: 'UNPAID' },
  sent:            { cls: 'bg-sky-100 text-sky-700 border-sky-200',             label: 'SENT' },
  overdue:         { cls: 'bg-rose-100 text-rose-800 border-rose-300',          label: 'OVERDUE' },
  draft:           { cls: 'bg-slate-100 text-slate-600 border-slate-200',       label: 'DRAFT' },
  void:            { cls: 'bg-slate-200 text-slate-500 border-slate-300',       label: 'VOID' },
  rejected:        { cls: 'bg-rose-100 text-rose-800 border-rose-300',          label: 'REJECTED' },
  cn_cleared:      { cls: 'bg-violet-100 text-violet-800 border-violet-200',    label: 'CN CLEARED' },
  written_off:     { cls: 'bg-slate-100 text-slate-700 border-slate-200',       label: 'WRITTEN OFF' },
};

const StatusPill = ({ status }) => {
  const key = (status || '').toLowerCase();
  const s = STATUS_STYLES[key] || STATUS_STYLES.draft;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold tracking-wide ${s.cls}`}>
      {s.label}
    </span>
  );
};

// ── Reusable KPI tile ─────────────────────────────────────
const KpiTile = ({ icon: Icon, label, value, sub, tone = 'slate', testId }) => {
  const toneMap = {
    slate:   'border-slate-200 bg-white',
    violet:  'border-violet-200 bg-violet-50/60 text-violet-900',
    emerald: 'border-emerald-200 bg-emerald-50/60 text-emerald-900',
    rose:    'border-rose-200 bg-rose-50/60 text-rose-900',
    amber:   'border-amber-200 bg-amber-50/60 text-amber-900',
    sky:     'border-sky-200 bg-sky-50/60 text-sky-900',
  };
  return (
    <div className={`rounded-lg border ${toneMap[tone]} px-3 py-2.5`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{label}</div>
        <Icon className="w-3.5 h-3.5 opacity-60" />
      </div>
      <div className="text-base font-bold mt-0.5">{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
};

// ── Payment timeline row ──────────────────────────────────
const TimelineRow = ({ icon: Icon, tone, title, amount, sub, right }) => {
  const toneMap = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    rose:    'bg-rose-50 border-rose-200 text-rose-900',
    slate:   'bg-slate-50 border-slate-200 text-slate-800',
  };
  return (
    <div className={`flex items-center justify-between gap-3 rounded-md border ${toneMap[tone]} px-3 py-2`}>
      <div className="flex items-center gap-2 min-w-0">
        <div className={`w-7 h-7 rounded-full grid place-content-center border ${toneMap[tone]}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold truncate">{title}</div>
          {sub && <div className="text-[10px] opacity-70 truncate">{sub}</div>}
        </div>
      </div>
      <div className="text-right">
        <div className="text-[12px] font-bold tabular-nums">{amount}</div>
        {right && <div className="text-[10px] opacity-70">{right}</div>}
      </div>
    </div>
  );
};

export default function InvoiceDetailModal({ open, onClose, invoiceId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !invoiceId) return;
    setLoading(true);
    setData(null);
    api.get(`/books/revenue-report/invoice-detail/${invoiceId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load invoice detail'))
      .finally(() => setLoading(false));
  }, [open, invoiceId]);

  // Per-invoice PDF export — KPIs + timeline (payments + CN with reason) +
  // expected-date history. Uses jsPDF (dynamic import to keep bundle lean).
  const exportInvoicePdf = async (d) => {
    if (!d) return;
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import('jspdf'), import('jspdf-autotable')
    ]);
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 36;
    const inv = d.invoice, k = d.kpis || {};
    const inr = (n) => Math.round(n || 0).toLocaleString('en-IN');

    // Title
    doc.setFontSize(15); doc.setTextColor(15, 23, 42);
    doc.text(inv.invoice_number || 'Invoice', margin, 46);
    doc.setFontSize(10); doc.setTextColor(90, 90, 110);
    doc.text(`Status: ${(inv.status || '').toUpperCase()}`, margin, 62);
    if ((k.cn_applied || 0) > 0) doc.text(`· CN applied Rs ${inr(k.cn_applied)}`, margin + 120, 62);
    doc.setFontSize(8);
    doc.text(`Generated ${new Date().toLocaleString('en-IN')}`, pageW - margin, 46, { align: 'right' });

    // Meta strip
    doc.setFontSize(9); doc.setTextColor(30);
    const meta = [
      `Customer: ${inv.customer_name || '-'}`,
      `Salesperson: ${inv.salesperson_name || '-'}`,
      `Branch: ${inv.cf_branch_name || '-'}  ·  Client type: ${inv.cf_client_type || '-'}`,
      `Issued: ${inv.date || '-'}  →  Due: ${inv.due_date || '-'}`,
    ];
    meta.forEach((line, i) => doc.text(line, margin, 84 + i * 14));

    // KPI grid — 4 columns x 2 rows
    const kpiY = 84 + meta.length * 14 + 8;
    const tiles = [
      { label: 'Sales',          value: `Rs ${inr(k.sales)}`,       fill: [124, 58, 237] },
      { label: 'Payments',       value: `Rs ${inr(k.payments)}`,    sub: `${k.payments_count || 0} pmt(s)`, fill: [16, 185, 129] },
      { label: 'CN Applied',     value: `Rs ${inr(k.cn_applied)}`,  sub: `${k.credit_notes_count || 0} note(s)`, fill: [244, 63, 94] },
      { label: 'Write-off',      value: `Rs ${inr(k.write_off)}`,   fill: [100, 116, 139] },
      { label: 'Outstanding',    value: `Rs ${inr(k.outstanding)}`, sub: k.outstanding > 0 ? 'Balance due' : 'Cleared', fill: [217, 119, 6] },
      { label: 'Days since',     value: `${k.days_since_issued ?? '-'}d`, fill: [148, 163, 184] },
      { label: 'Days overdue',   value: k.days_overdue === null ? '-' : `${k.days_overdue}d`, fill: k.days_overdue > 0 ? [244, 63, 94] : [16, 185, 129] },
      { label: 'Collection %',   value: `${k.sales ? Math.round(100 * k.payments / k.sales) : 0}%`, fill: [14, 165, 233] },
    ];
    const tileW = (pageW - margin*2 - 12) / 4;
    tiles.forEach((t, i) => {
      const col = i % 4, row = Math.floor(i / 4);
      const x = margin + col * (tileW + 4);
      const y = kpiY + row * 46;
      doc.setFillColor(...t.fill);
      doc.roundedRect(x, y, tileW, 42, 3, 3, 'F');
      doc.setTextColor(255); doc.setFontSize(7);
      doc.text(t.label.toUpperCase(), x + 8, y + 12);
      doc.setFontSize(11);
      doc.text(t.value, x + 8, y + 26);
      if (t.sub) { doc.setFontSize(7); doc.text(t.sub, x + 8, y + 37); }
    });

    // Payment timeline table (payments + CNs with reason)
    let curY = kpiY + 100;
    doc.setTextColor(15, 23, 42); doc.setFontSize(10);
    doc.text('Payment timeline', margin, curY);
    curY += 6;
    const events = [];
    for (const p of (d.payments || [])) {
      events.push([
        p.date || '-',
        (p.is_void ? '[VOID] ' : '') + `Payment ${p.payment_number || ''}`,
        `${p.mode || ''}${p.reference ? ' · ref ' + p.reference : ''}${p.created_by_name ? ' · by ' + p.created_by_name : ''}`,
        p.is_void ? '(void)' : `+ Rs ${inr(p.amount_applied)}`,
      ]);
    }
    for (const c of (d.credit_notes || [])) {
      events.push([
        c.date || '-',
        `Credit note ${c.creditnote_number || ''}`,
        `${c.reason || 'No reason recorded'}${c.created_by_name ? ' · by ' + c.created_by_name : ''}`,
        `- Rs ${inr(c.amount_applied)}`,
      ]);
    }
    events.sort((a, b) => (a[0] || '').localeCompare(b[0] || ''));
    if (events.length === 0) events.push(['—', 'No payments or credit notes applied yet', '', '']);
    autoTable(doc, {
      startY: curY + 4,
      head: [['Date', 'Type', 'Detail', 'Amount']],
      body: events,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [124, 58, 237], textColor: 255 },
      columnStyles: { 0: { cellWidth: 62 }, 3: { halign: 'right', cellWidth: 72 } },
    });
    curY = doc.lastAutoTable.finalY + 14;

    // Expected date history
    const history = d.expected_date_events || [];
    if (history.length > 0) {
      doc.setTextColor(15, 23, 42); doc.setFontSize(10);
      doc.text('Expected payment history', margin, curY);
      autoTable(doc, {
        startY: curY + 4,
        head: [['Changed on', 'New expected', 'By', 'Reason']],
        body: history.map((e) => [
          e.changed_at || e.changed_on || '-',
          e.new_expected_date || '-',
          e.actor || '-',
          e.reason || '-',
        ]),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [217, 119, 6], textColor: 255 },
      });
    }

    doc.save(`${(inv.invoice_number || 'invoice').replace(/[^a-z0-9]+/gi, '_')}.pdf`);
  };

  const inv = data?.invoice;
  const k = data?.kpis || {};
  const payments = data?.payments || [];
  const cns = data?.credit_notes || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap pr-8">
            <span className="text-violet-700 font-mono">{inv?.invoice_number || 'Invoice'}</span>
            {inv?.status && <StatusPill status={inv.status} />}
            {(k?.cn_applied || 0) > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 text-rose-700 px-2 py-0.5 text-[9px] font-bold"
                title={`Credit note applied: ${fmtInrFull(k.cn_applied)} across ${k.credit_notes_count} entry(s)`}
                data-testid="invoice-cn-tag"
              >
                <FileWarning className="w-2.5 h-2.5" /> CN
              </span>
            )}
            {(k?.write_off || 0) > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 text-slate-700 px-2 py-0.5 text-[9px] font-bold"
                title={`Write-off applied: ${fmtInrFull(k.write_off)}`}
                data-testid="invoice-wo-tag"
              >
                <Eraser className="w-2.5 h-2.5" /> W/OFF
              </span>
            )}
            {(k?.voided_payments_count || 0) > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 text-slate-500 px-2 py-0.5 text-[9px] font-bold"
                title={`${k.voided_payments_count} voided payment(s) worth ${fmtInrFull(k.voided_payments_amount)}`}
                data-testid="invoice-void-tag"
              >
                <Ban className="w-2.5 h-2.5" /> VOID
              </span>
            )}
            <Button
              size="sm" variant="ghost"
              className="ml-auto h-6 px-2 text-[11px] gap-1 text-slate-500 hover:text-violet-700"
              onClick={() => exportInvoicePdf(data)}
              disabled={!data}
              data-testid="invoice-pdf-btn"
            >
              <FileText className="w-3 h-3" /> PDF
            </Button>
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading invoice…
          </div>
        )}

        {!loading && data && (
          <div className="space-y-4">
            {/* ── Meta strip ─────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 text-[11px]">
              <div className="border border-slate-100 rounded-md p-2 bg-slate-50/40">
                <div className="text-[9px] uppercase font-bold text-slate-500">Customer</div>
                <div className="font-medium text-slate-800 truncate" title={inv.customer_name}>
                  <User className="w-3 h-3 inline mr-1 text-slate-400" />
                  {inv.customer_name || '—'}
                </div>
              </div>
              <div className="border border-slate-100 rounded-md p-2 bg-slate-50/40">
                <div className="text-[9px] uppercase font-bold text-slate-500">Salesperson</div>
                <div className="font-medium text-slate-800 truncate">
                  <Tag className="w-3 h-3 inline mr-1 text-slate-400" />
                  {inv.salesperson_name || '— Unassigned —'}
                </div>
              </div>
              <div className="border border-slate-100 rounded-md p-2 bg-slate-50/40">
                <div className="text-[9px] uppercase font-bold text-slate-500">Branch · Client type</div>
                <div className="font-medium text-slate-800 truncate">
                  <Building2 className="w-3 h-3 inline mr-1 text-slate-400" />
                  {inv.cf_branch_name || '—'} · {inv.cf_client_type || '—'}
                </div>
              </div>
              <div className="border border-slate-100 rounded-md p-2 bg-slate-50/40">
                <div className="text-[9px] uppercase font-bold text-slate-500">Enrolment</div>
                <div className="font-medium text-slate-800 truncate flex items-center gap-1" title={inv.student_name || ''}>
                  <GraduationCap className="w-3 h-3 text-slate-400 shrink-0" />
                  {inv.enrolment_no ? (
                    <a
                      href={`/training/students?focus=${encodeURIComponent(inv.enrolment_no)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-emerald-700 hover:text-emerald-800 hover:underline font-mono tabular-nums"
                      data-testid="invoice-enrolment-inline"
                    >
                      {inv.enrolment_no}
                    </a>
                  ) : <span className="text-slate-400">—</span>}
                  {inv.student_name && (
                    <span className="text-[10px] text-slate-500 truncate ml-1">· {inv.student_name}</span>
                  )}
                </div>
              </div>
              <div className="border border-slate-100 rounded-md p-2 bg-slate-50/40">
                <div className="text-[9px] uppercase font-bold text-slate-500">Issued · Due</div>
                <div className="font-medium text-slate-800">
                  <Calendar className="w-3 h-3 inline mr-1 text-slate-400" />
                  {fmtDate(inv.date)} → {fmtDate(inv.due_date)}
                </div>
              </div>
            </div>

            {/* ── KPI cards ──────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <KpiTile icon={IndianRupee} label="Sales"       value={fmtInrFull(k.sales)}       tone="violet"  testId="idm-kpi-sales" />
              <KpiTile icon={Wallet}      label="Payments"    value={fmtInrFull(k.payments)}    tone="emerald"
                       sub={`${k.payments_count} payment${k.payments_count === 1 ? '' : 's'}`}   testId="idm-kpi-payments" />
              <KpiTile icon={FileWarning} label="CN Applied"  value={fmtInrFull(k.cn_applied)}  tone="rose"
                       sub={`${k.credit_notes_count} note${k.credit_notes_count === 1 ? '' : 's'}`} testId="idm-kpi-cn" />
              <KpiTile icon={Eraser}      label="Write-off"   value={fmtInrFull(k.write_off)}   tone="slate"   testId="idm-kpi-wo" />
              <KpiTile icon={AlertTriangle} label="Outstanding" value={fmtInrFull(k.outstanding)} tone={k.outstanding > 0 ? 'amber' : 'emerald'}
                       sub={k.outstanding <= 0 ? 'Cleared' : 'Balance due'} testId="idm-kpi-outstanding" />
              <KpiTile icon={Clock}       label="Days since issued" value={`${k.days_since_issued ?? '—'}d`} tone="slate"
                       sub={inv.date ? `Issued ${fmtDate(inv.date)}` : ''} testId="idm-kpi-days-issued" />
              <KpiTile icon={CircleAlert} label="Days overdue"      value={k.days_overdue === null ? '—' : `${k.days_overdue}d`}
                       tone={k.days_overdue > 0 ? 'rose' : 'emerald'}
                       sub={k.days_overdue > 0 ? `Due ${fmtDate(inv.due_date)}` : (k.outstanding > 0 ? 'Not overdue' : 'Cleared')}
                       testId="idm-kpi-days-overdue" />
              <KpiTile icon={TrendingUp}  label="Collection %" value={`${k.sales ? Math.round(100 * k.payments / k.sales) : 0}%`}
                       tone="sky" sub={`${fmtInr(k.payments)} of ${fmtInr(k.sales)}`} testId="idm-kpi-collection" />
            </div>

            {/* ── Payment timeline ───────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[11px] font-bold uppercase text-slate-600 tracking-wide flex items-center gap-1.5">
                  <Wallet className="w-3.5 h-3.5 text-emerald-600" /> Payment timeline
                </h3>
                <span className="text-[10px] text-slate-500">{payments.length + cns.length} event(s)</span>
              </div>
              {(payments.length + cns.length) === 0 ? (
                <div className="border border-dashed border-slate-200 rounded-md py-6 text-center text-[11px] text-slate-500">
                  No payments or credit notes applied yet.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {payments.map((p) => (
                    <TimelineRow
                      key={`pmt-${p.payment_id}`}
                      icon={p.is_void ? Ban : CheckCircle2}
                      tone={p.is_void ? 'slate' : 'emerald'}
                      title={
                        <span className={p.is_void ? 'line-through opacity-70' : ''}>
                          Payment {p.payment_number || ''}
                          {p.is_void && <span className="ml-1.5 text-[9px] font-bold not-italic bg-slate-700 text-white rounded px-1 py-0.5 no-underline inline-block">VOID</span>}
                        </span>
                      }
                      sub={
                        <>
                          {fmtDate(p.date)} · {p.mode}{p.reference ? ' · ref ' + p.reference : ''}
                          {p.created_by_name && (
                            <>{' · '}<span className="opacity-90">by <b>{p.created_by_name}</b></span></>
                          )}
                          {p.is_void && <span className="ml-1 text-slate-500 italic">— not counted in KPIs</span>}
                        </>
                      }
                      amount={
                        <span className={p.is_void ? 'line-through text-slate-400' : ''}>
                          {p.is_void ? '' : '+ '}{fmtInrFull(p.amount_applied)}
                        </span>
                      }
                    />
                  ))}
                  {cns.map((c) => (
                    <TimelineRow
                      key={`cn-${c.creditnote_id}-${c.creditnote_number}`}
                      icon={FileWarning}
                      tone="rose"
                      title={`Credit note ${c.creditnote_number || ''}`}
                      sub={
                        <>
                          {fmtDate(c.date)}
                          {c.reason ? ' · ' + c.reason : ''}
                          {c.created_by_name && (
                            <>{' · '}<span className="opacity-90">by <b>{c.created_by_name}</b></span></>
                          )}
                        </>
                      }
                      amount={`− ${fmtInrFull(c.amount_applied)}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* ── Expected date change history ─────── */}
            {(data.expected_date_events || []).length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-[11px] font-bold uppercase text-slate-600 tracking-wide flex items-center gap-1.5">
                    <History className="w-3.5 h-3.5 text-amber-600" /> Expected payment history
                  </h3>
                  <span className="text-[10px] text-slate-500">{data.expected_date_events.length} change(s)</span>
                </div>
                <div className="space-y-1.5">
                  {data.expected_date_events.map((ev, i) => (
                    <div key={i} className="rounded-md border border-amber-100 bg-amber-50/40 px-3 py-2 text-[11px]" data-testid={`exp-history-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-amber-900">
                          Expected → {fmtDate(ev.new_expected_date)}
                        </div>
                        <div className="text-[10px] text-slate-500 whitespace-nowrap">
                          {ev.changed_at || ev.changed_on}
                        </div>
                      </div>
                      {ev.actor && (
                        <div className="text-[10px] text-slate-600">by <b>{ev.actor}</b></div>
                      )}
                      {ev.reason && (
                        <div className="text-[10px] text-slate-500 italic mt-0.5 truncate" title={ev.reason}>
                          "{ev.reason}"
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Expected payment info + Zoho CTA ──── */}
            <div className="border-t border-slate-100 pt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] text-slate-500 min-w-0">
                {(inv.expected_payment_date || inv.original_expected_payment_date) && (
                  <>
                    <span className="font-medium text-slate-600">Expected payment:</span>{' '}
                    {fmtDate(inv.expected_payment_date)}
                    {inv.original_expected_payment_date && inv.original_expected_payment_date !== inv.expected_payment_date && (
                      <span className="ml-2 text-slate-400">
                        (originally {fmtDate(inv.original_expected_payment_date)})
                      </span>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {inv.enrolment_no && (
                  <a
                    href={`/training/students?focus=${encodeURIComponent(inv.enrolment_no)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[12px] font-semibold px-3 py-1.5 transition"
                    data-testid="invoice-training-link"
                    title={inv.student_name ? `${inv.student_name} · Enrolment ${inv.enrolment_no}` : `Enrolment ${inv.enrolment_no}`}
                  >
                    <GraduationCap className="w-3.5 h-3.5" />
                    Open Student <span className="opacity-70 font-normal">· {inv.enrolment_no}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {data?.zoho_url && (
                  <a
                    href={data.zoho_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-violet-300 bg-violet-50 hover:bg-violet-100 text-violet-700 text-[12px] font-semibold px-3 py-1.5 transition"
                    data-testid="invoice-zoho-link"
                  >
                    Open in Zoho Books <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
